import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'https://api.rwfngo.com/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = config.headers;
    if (headers && typeof headers.set === 'function') {
      headers.set('Content-Type', false);
    } else if (headers) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }
  return config;
});

export const userService = {
  register: (userData) => apiClient.post('/users/register', userData),
  getUser: (userId) => apiClient.get(`/users/${userId}`),
  updateUser: (userId, userData) => apiClient.put(`/users/${userId}`, userData),
};

export const teamMemberService = {
  getAll: (params = {}) => apiClient.get('/team-members', { params }),
};

export const homeSliderService = {
  getAll: () => apiClient.get('/home-sliders'),
};

export const homePopupService = {
  getActive: () => apiClient.get('/home-popups'),
};

export const campaignCategoryService = {
  getAll: () => apiClient.get('/campaign-categories'),
};

export const campaignService = {
  submitCampaign: (formData) => apiClient.post('/campaigns/submit', formData),
  getAllCampaigns: (params = {}) => {
    // Allow legacy string form: getAllCampaigns('active')
    const normalized =
      typeof params === 'string' ? { status: params } : (params && typeof params === 'object' ? params : {});
    const q = { status: 'active', ...normalized };
    Object.keys(q).forEach((k) => {
      if (q[k] === undefined || q[k] === null || q[k] === '') delete q[k];
    });
    return apiClient.get('/campaigns', { params: q });
  },
  getCampaignBySlug: (slug) =>
    apiClient.get(`/campaigns/slug/${encodeURIComponent(String(slug ?? '').trim())}`),
  getCampaignById: (id) => apiClient.get(`/campaigns/${id}`),
  /** Public sponsor tiers — use when slug payload omits `products` (older API / proxies). */
  getCampaignProducts: (campaignId) => apiClient.get(`/campaigns/${campaignId}/products`),
  getCampaignEssentials: (campaignId) => apiClient.get(`/campaigns/${campaignId}/essentials`),
};

export const campaignRatingService = {
  getApprovedByCampaign: (campaignId) => apiClient.get(`/campaigns/${campaignId}/ratings`),
  getPublic: () => apiClient.get('/ratings/public'),
  submitRating: (campaignId, data) => apiClient.post(`/campaigns/${campaignId}/ratings`, data),
};

export const donationService = {
  initiateDonation: (donationData) => apiClient.post('/donations/initiate', donationData),
  verifyDonation: (verificationData) => apiClient.post('/donations/verify', verificationData),
  failDonation: (donationId, reason) =>
    apiClient.post(`/donations/${donationId}/fail`, { reason: reason || 'cancelled' }),
  getDonationsByUser: (userId) => apiClient.get(`/donations/user/${userId}`),
  getDonationsByCampaign: (campaignId) => apiClient.get(`/donations/campaign/${campaignId}`),
  getDonationById: (donationId) => apiClient.get(`/donations/${donationId}`),
  getOrCreateUser: (userData) => apiClient.post('/users/register', userData),
};

export const volunteerService = {
  submitApplication: (applicationData) => apiClient.post('/volunteers', applicationData),
};

export const contactService = {
  submitInquiry: (inquiryData) => apiClient.post('/contact', inquiryData),
};

export const galleryService = {
  getGallery: (category) => apiClient.get('/gallery', { params: { category } }),
  getGalleryById: (id) => apiClient.get(`/gallery/${id}`),
  getGalleryByCampaign: (campaignId) => apiClient.get(`/gallery/campaign/${campaignId}`),
};

export const awardService = {
  getAllAwards: () => apiClient.get('/awards'),
};

export const csrService = {
  getCSRActivities: () => apiClient.get('/csr'),
};

export const siteStatService = {
  getAll: () => apiClient.get('/site-stats'),
};

export const testimonialService = {
  getAll: () => apiClient.get('/testimonials'),
};

export const newsletterService = {
  subscribe: (email, source = 'footer') => apiClient.post('/newsletters', { email, source }),
};

export const legalPageService = {
  getAll: () => apiClient.get('/legal-pages'),
  getBySlug: (slug) => apiClient.get(`/legal-pages/${slug}`),
};

export const financialDocumentService = {
  getAll: () => apiClient.get('/financial-documents'),
};

export default apiClient;
